import {
  Day,
  DragAndDrop,
  type DragEventArgs,
  Inject,
  type RenderCellEventArgs,
  Resize,
  ResourceDirective,
  ResourcesDirective,
  ScheduleComponent,
  ViewDirective,
  ViewsDirective,
} from '@syncfusion/ej2-react-schedule'
import { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import './scheduleCalendar.scss'

import sendMessageIcon from 'assets/icons/send-message.svg'
import { Button } from 'common/components/Button/Button'
import { Show } from 'common/components/Show/Show'
import { ALT_CONSTANTS } from 'common/constants/altConstants'
import { BUTTON_CONSTANTS, BUTTON_MODIFIER } from 'common/constants/buttonConstants'
import type { ObjectType } from 'common/interfaces/object'
import { DateService } from 'common/services/dateService'
import dayjs from 'dayjs'
import { AnnounceIconButton } from 'features/Home/Book/Booking/Schedule/Announce/announce'
import { AnnounceModal } from 'features/Home/Book/Booking/Schedule/Announce/announce-modal'
import { PatientScheduleSkeleton } from 'features/Home/Book/Booking/Schedule/PatientSchedule/PatientScheduleSkeleton/PatientScheduleSkeleton'
import { SendSMSConfirmation } from 'features/Home/Book/Booking/Schedule/PatientSchedule/Calendar/SendSMSConfirmation/SendSMSConfirmation'
import { Warning } from 'features/Home/Book/Booking/Schedule/PatientSchedule/Calendar/Warning/Warning'
import { INFO_CONSTANTS, MIN_PRE_POPULATE_TIME } from 'features/Home/constants/infoConstants'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { useParams } from 'react-router'
import { useToggle } from 'react-use'
import { useScheduleHeight } from '../useScheduleHeight'
import { getDropPointElement } from './helpers'
import type { ScheduleCalendarProps } from './scheduleCalendar.types'
import { useFrozenValue } from './useFrozenValue'
import { useInteractionFreeze } from './useInteractionFreeze'
import { useScheduleCalendarReady } from './useScheduleCalendarReady'
import { useScheduleCalendar } from './useScheduleCalendar'
import { useScheduleView } from './useScheduleView'
import { useStableResourceDataSource } from './useStableResourceDataSource'
import { useStableScheduleDataSource } from './useStableScheduleDataSource'

export const ScheduleCalendar = memo(
  ({
    createCustomAppointment,
    setEditedPatient,
    handleUpdateEvents,
    sendInfoPatientsNotification = async () => {},
    prepopulateTimeOnClick = MIN_PRE_POPULATE_TIME,
    showConsultationStaffInitials = false,
    resources = [],
    dayInterval,
    disabledIntervals,
    timeZone,
    rooms,
    schedules,
    isSendingNotifications,
    eventsToSendNotification,
    patientStatuses,
    appointmentCategories,
    appointmentColor,
    hasEhrBooking,
  }: ScheduleCalendarProps) => {
    const { day } = useParams()
    const interval = 15
    const [showBusyNotification, setShowBusyNotification] = useToggle(false)
    const [isSendSmsConfirmVisible, setIsSendSmsConfirmVisible] = useToggle(false)
    const [openAnnounceModal, toggleAnnounceModal] = useToggle(false)

    const scheduleObj = useRef<ScheduleComponent>(null)
    const scheduleContainerRef = useRef<HTMLDivElement>(null)

    const formattedDate = useMemo(() => {
      if (!timeZone || !day) {
        return null
      }

      return dayjs(day).tz(timeZone, true).format('YYYY-MM-DD')
    }, [timeZone, day])

    const {
      events,
      awaitConfirmationEvent,
      handleScheduleChange,
      setAwaitConfirmationEvent,
      proceedToUpdateEvents,
      handleSelect,
    } = useScheduleCalendar({
      schedules,
      dayInterval,
      disabledIntervals,
      formattedDate,
      timeZone,
      rooms,
      scheduleObj,
      prepopulateTimeOnClick,
      createCustomAppointment,
      handleUpdateEvents,
    })

    const { isInteracting, handleInteractionStart, handleInteractionEnd } = useInteractionFreeze()
    const { isReady, markReady } = useScheduleCalendarReady(day)

    const data = useFrozenValue(useStableScheduleDataSource(events), isInteracting)
    const resourceDataSource = useFrozenValue(useStableResourceDataSource(resources), isInteracting)

    const {
      eventRendered,
      eventTemplate,
      handleEventClick,
      handleChange,
      handleDataBound,
      handleActionFailure,
      handlePopupOpen,
      handleRemoveSelectedCells,
      reapplyAppointmentColors,
      selectedDate,
      timeScale,
      workHours,
      eventSettings,
    } = useScheduleView({
      data,
      schedules,
      scheduleObj,
      interval,
      dayInterval,
      day,
      timeZone,
      appointmentColor,
      appointmentCategories,
      patientStatuses,
      hasEhrBooking,
      showConsultationStaffInitials,
      setEditedPatient,
      handleScheduleChange,
    })

    const handleDragStop = useCallback(
      (args: DragEventArgs) => {
        handleInteractionEnd()

        const dropped = getDropPointElement(args)
        const groupEl = dropped?.closest('[data-group-index]')
        const eventData = args.data as unknown as IPatientSchedule | undefined
        if (!groupEl || !eventData?.room_id || !eventData.id) {
          requestAnimationFrame(() => reapplyAppointmentColors())
          return
        }

        const groupIndex = Number(groupEl.getAttribute('data-group-index'))
        const dropResourceId = resourceDataSource[groupIndex]?.resourceId
        if (!dropResourceId || dropResourceId === eventData.room_id) {
          requestAnimationFrame(() => reapplyAppointmentColors())
          return
        }

        args.cancel = true
        handleScheduleChange({ ...eventData, room_id: dropResourceId }).finally(() => {
          requestAnimationFrame(() => reapplyAppointmentColors())
        })
      },
      [handleInteractionEnd, resourceDataSource, handleScheduleChange, reapplyAppointmentColors],
    )

    const handleCalendarDataBound = useCallback(() => {
      handleDataBound()
      markReady()
    }, [handleDataBound, markReady])

    const resourceHeaderTemplate = useCallback(
      (props: ObjectType) => {
        const selectedResource = props.resourceData.resourceId
        return (
          resources.find(({ resourceId }) => resourceId === selectedResource)?.resourceTemplate ||
          ''
        )
      },
      [resources],
    )

    const indentTemplate = useCallback(() => {
      if (!dayjs(day).isSame(dayjs(), 'day')) return <></>
      return (
        <div className='center'>
          <AnnounceIconButton onClick={toggleAnnounceModal} />
        </div>
      )
      // Deps must match what's used (day/toggle) — NOT `rooms`. Depending on `rooms` made
      // this template's identity churn on every refetch, triggering a full schedule layout
      // rebuild (headerIndentTemplate change → isLayout) on drag/drop and modal edits.
    }, [day, toggleAnnounceModal])

    const handleSendNotifications = useCallback(async () => {
      await sendInfoPatientsNotification(eventsToSendNotification)
      setIsSendSmsConfirmVisible(false)
    }, [eventsToSendNotification])

    const handleToggleSendSmsConfirmVisible = useCallback(
      (event: boolean) => () => {
        setIsSendSmsConfirmVisible(event)
      },
      [],
    )

    const handleToggleSendBusyNotification = useCallback(
      (event: boolean) => () => {
        setShowBusyNotification(event)
      },
      [],
    )

    const handleRenderCell = useCallback(
      (args: RenderCellEventArgs) => {
        if (args.elementType !== 'workCells') {
          return
        }

        const resource = resources[args.groupIndex]
        const room = rooms.find((room: ObjectType) => {
          return resource?.resourceId === `${room.label}/${room.value}`
        })

        if (!room || !room.slots) {
          return
        }

        const cellStartTime = new Date(args.date)
        const cellEndTime = new Date(cellStartTime)

        const isAvailable = room.slots.some((slot: ObjectType) => {
          const slotStart = new Date(args.date)
          slotStart.setHours(
            parseInt(slot.start_time.split(':')[0], 10),
            parseInt(slot.start_time.split(':')[1], 10),
            0,
            0,
          )

          const slotEnd = new Date(args.date)
          slotEnd.setHours(
            parseInt(slot.end_time.split(':')[0], 10),
            parseInt(slot.end_time.split(':')[1], 10),
            0,
            0,
          )

          return cellStartTime >= slotStart && cellEndTime < slotEnd
        })

        if (!isAvailable) {
          args.element.classList.add('e-disabled-cell')
        }
      },
      [resources, rooms],
    )

    const group = useMemo(() => ({ resources: ['Rooms'], enableCompactView: false }), [])

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          scheduleContainerRef.current &&
          !scheduleContainerRef.current.contains(event.target as Node)
        ) {
          handleRemoveSelectedCells()
        }
      }

      document.addEventListener('click', handleClickOutside)

      return () => {
        document.removeEventListener('click', handleClickOutside)
      }
    }, [handleRemoveSelectedCells])

    const scheduleHeight = useScheduleHeight()

    return (
      <div className='schedule-control-section' ref={scheduleContainerRef}>
        {!isReady && (
          <div className='schedule-skeleton-overlay'>
            <PatientScheduleSkeleton />
          </div>
        )}
        <div className={isReady ? undefined : 'schedule-pending'}>
          <div className='col-lg-12 control-section'>
            <div className={`control-wrapper ${!timeZone ? 'loading' : ''}`}>
              {resources.length > 0 && timeZone && (
                <ScheduleComponent
                  key={day}
                  ref={scheduleObj}
                  cssClass='room-schedule'
                  width='100%'
                  height={scheduleHeight}
                  timezone={timeZone}
                  timeScale={timeScale}
                  selectedDate={selectedDate}
                  startHour={dayInterval.min as string}
                  endHour={dayInterval.max as string}
                  workHours={workHours}
                  currentView='Day'
                  headerIndentTemplate={indentTemplate}
                  resourceHeaderTemplate={resourceHeaderTemplate}
                  eventRendered={eventRendered}
                  showQuickInfo={false}
                  eventClick={handleEventClick}
                  actionBegin={handleChange}
                  dataBound={handleCalendarDataBound}
                  actionFailure={handleActionFailure}
                  showHeaderBar={false}
                  select={handleSelect}
                  renderCell={handleRenderCell}
                  popupOpen={handlePopupOpen}
                  allowDragAndDrop
                  dragStart={handleInteractionStart}
                  dragStop={handleDragStop}
                  resizeStart={handleInteractionStart}
                  resizeStop={handleInteractionEnd}
                  eventSettings={eventSettings}
                  group={group}>
                  <ResourcesDirective>
                    <ResourceDirective
                      field='room_id'
                      title='Rooms'
                      name='Rooms'
                      dataSource={resourceDataSource}
                      idField='resourceId'
                    />
                  </ResourcesDirective>
                  <ViewsDirective>
                    <ViewDirective option='Day' eventTemplate={eventTemplate} />
                  </ViewsDirective>
                  <Inject services={[Day, Resize, DragAndDrop]} />
                </ScheduleComponent>
              )}

              <Show
                when={
                  !DateService.isPast(new Date(`${formattedDate} 23:59`), timeZone) &&
                  eventsToSendNotification.length > 0
                }>
                <div className='action'>
                  <Button
                    modifier={BUTTON_MODIFIER.QUATERNARY}
                    onClick={handleToggleSendSmsConfirmVisible(true)}
                    loading={isSendingNotifications}>
                    <span className='center' style={{ gap: 8 }}>
                      {!isSendingNotifications && (
                        <img
                          className='actionIcon'
                          src={sendMessageIcon}
                          alt={ALT_CONSTANTS.ACTION_ICON}
                        />
                      )}
                      <span>Notify patients</span>
                    </span>
                  </Button>
                </div>
              </Show>
            </div>
          </div>

          <Warning
            open={Boolean(awaitConfirmationEvent)}
            title={INFO_CONSTANTS.ARE_YOU_SURE}
            warning={INFO_CONSTANTS.MULTIPLE_PATIENTS_TITLE}
            okButtonText={INFO_CONSTANTS.YES}
            handleClose={() => setAwaitConfirmationEvent(null)}
            handleConfirm={proceedToUpdateEvents}
            hasCancel
          />

          <Warning
            open={showBusyNotification}
            title={INFO_CONSTANTS.INFO}
            warning={INFO_CONSTANTS.BUSY_ROOM_WARNING}
            handleClose={handleToggleSendBusyNotification(false)}
            okButtonText={BUTTON_CONSTANTS.CLOSE}
            handleConfirm={handleToggleSendBusyNotification(false)}
          />

          <SendSMSConfirmation
            open={isSendSmsConfirmVisible}
            handleConfirm={handleSendNotifications}
            handleClose={handleToggleSendSmsConfirmVisible(false)}
          />
          <AnnounceModal
            open={openAnnounceModal}
            toggleOpen={toggleAnnounceModal}
            rooms={rooms.map((room) => room.id).filter((id) => !!id)}
          />
        </div>
      </div>
    )
  },
)
